import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PayeeRef } from '@/api/transactions';

interface PayeePrimaryProps {
  /** Bank text — always shown; never replaced (MM-D-19). */
  bankText: string;
  payeeRef: PayeeRef | null;
  /** When true, bank text alone is primary (no entity). */
  struck?: boolean;
  /** Inline after the name (the compact AI pill for an AI-produced name, TMM-B9). */
  nameAdornment?: ReactNode;
}

/**
 * Owner-register primary/secondary payee display: entity name when resolved, bank text always
 * visible beside or beneath it.
 */
export function PayeePrimary({
  bankText,
  payeeRef,
  struck = false,
  nameAdornment,
}: PayeePrimaryProps) {
  const { t } = useTranslation();
  const name = (text: string) => {
    const label = (
      <Typography component="span" sx={{ textDecoration: struck ? 'line-through' : undefined }}>
        {text}
      </Typography>
    );
    if (nameAdornment === undefined || nameAdornment === null) return label;
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        {label}
        {nameAdornment}
      </Box>
    );
  };
  if (payeeRef === null) return name(bankText);
  return (
    <Stack spacing={0}>
      {name(payeeRef.name)}
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {t('payee.bankText', { text: bankText })}
      </Typography>
    </Stack>
  );
}
